import { useEffect, useRef } from 'react'
import confetti from 'canvas-confetti'

interface Props {
  intensity?: number
  active: boolean
}

export function ConfettiLayer({ intensity = 0.6, active }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const myConfetti = useRef<ReturnType<typeof confetti.create> | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
    myConfetti.current = confetti.create(canvas, { resize: true, useWorker: false })
    const onResize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!active || !myConfetti.current) return
    const fire = myConfetti.current
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const baseCount = Math.floor(80 + intensity * 240)
    const colors = [
      getComputedStyle(document.documentElement).getPropertyValue('--brand-primary').trim() ||
        '#238500',
      getComputedStyle(document.documentElement).getPropertyValue('--brand-accent').trim() ||
        '#FF793C',
      getComputedStyle(document.documentElement).getPropertyValue('--brand-secondary').trim() ||
        '#0B465D',
      '#ffffff',
    ]
    fire({
      particleCount: baseCount * 0.4,
      angle: 60,
      spread: 65,
      origin: { x: 0, y: 1 },
      colors,
      scalar: 1 / dpr,
    })
    fire({
      particleCount: baseCount * 0.4,
      angle: 120,
      spread: 65,
      origin: { x: 1, y: 1 },
      colors,
      scalar: 1 / dpr,
    })
    if (intensity >= 0.85) {
      fire({
        particleCount: baseCount * 0.6,
        angle: 90,
        spread: 100,
        startVelocity: 55,
        origin: { x: 0.5, y: 0.55 },
        colors,
        scalar: 1 / dpr,
      })
    }
  }, [active, intensity])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-50"
      style={{ width: '100vw', height: '100vh' }}
    />
  )
}
